import { getOperationalOrdersState } from "./operationalOrders";
import { getPaymentOperations } from "./paymentOperations";

export const STATISTICS_RANGE_OPTIONS = [
  { id: "today", label: "Hoy" },
  { id: "week", label: "7 dias" },
  { id: "month", label: "30 dias" },
  { id: "all", label: "Acumulado" },
];

const DAY_LABELS = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];

const CHANNEL_LABELS = {
  qr: "QR",
  app: "QR",
  manual: "Manual",
  waiter: "Mozo",
  mozo: "Mozo",
  assistant: "IA",
};

const PAYMENT_METHOD_LABELS = {
  deep_link: "Deep link",
  efectivo: "Efectivo",
  transferencia: "Transferencia",
  tarjeta: "Tarjeta",
};

const PAYMENT_STATUS_CASHED = new Set(["paid", "closed"]);

const toSafeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const formatDayKey = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildRangeWindow = (rangeId, nowDate = new Date()) => {
  const todayStart = startOfDay(nowDate);

  switch (rangeId) {
    case "today":
      return {
        id: rangeId,
        start: todayStart,
        end: addDays(todayStart, 1),
      };
    case "week":
      return {
        id: rangeId,
        start: addDays(todayStart, -6),
        end: addDays(todayStart, 1),
      };
    case "month":
      return {
        id: rangeId,
        start: addDays(todayStart, -29),
        end: addDays(todayStart, 1),
      };
    case "all":
    default:
      return {
        id: "all",
        start: null,
        end: null,
      };
  }
};

const isWithinWindow = (value, window) => {
  if (!value) {
    return false;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }

  if (!window?.start || !window?.end) {
    return true;
  }

  return date >= window.start && date < window.end;
};

const getOrderTimestamp = (order) =>
  order?.orderedAt || order?.requestedAt || order?.createdAt || null;

const getPaymentTimestamp = (operation) =>
  operation?.closedAt || operation?.paidAt || operation?.orderedAt || null;

const getPaymentGrossAmount = (operation) =>
  toSafeNumber(operation?.grossAmount ?? operation?.amount);

const getPaymentEconomics = (operation) =>
  operation?.economics && typeof operation.economics === "object"
    ? operation.economics
    : null;

const getOrderItemUnitPrice = (item) => {
  if (item?.unitPrice !== undefined) {
    return toSafeNumber(item.unitPrice);
  }

  return toSafeNumber(item?.baseUnitPrice) + toSafeNumber(item?.extraUnitCost);
};

const getOrderItemTotal = (item) =>
  getOrderItemUnitPrice(item) * Math.max(1, toSafeNumber(item?.quantity || 1));

const getOrderTotal = (order) => {
  const directTotal = toSafeNumber(order?.totalAmount);
  if (directTotal > 0) {
    return directTotal;
  }

  const pricingTotal = toSafeNumber(order?.pricing?.finalTotal);
  if (pricingTotal > 0) {
    return pricingTotal;
  }

  return (Array.isArray(order?.items) ? order.items : []).reduce(
    (sum, item) => sum + getOrderItemTotal(item),
    0
  );
};

const getOrderSourceLabel = (order) => {
  const sourceKey = String(order?.source || order?.canal || "")
    .trim()
    .toLowerCase();

  return CHANNEL_LABELS[sourceKey] || order?.canal || order?.source || "Salon";
};

const getPaymentMethodLabel = (operation) => {
  const key = operation?.selectedMethod || operation?.requestedMethod || "";
  return PAYMENT_METHOD_LABELS[key] || key || "Sin definir";
};

const getOrderProductName = (item) =>
  String(item?.productName || item?.name || item?.nombre || "Producto")
    .trim()
    .slice(0, 120);

const dedupeOrders = (orders) => {
  const map = new Map();

  (Array.isArray(orders) ? orders : []).forEach((order) => {
    if (!order?.id) {
      return;
    }

    if (!map.has(order.id)) {
      map.set(order.id, order);
      return;
    }

    const previousOrder = map.get(order.id);
    const previousPhase = String(previousOrder?.phase || "");
    const nextPhase = String(order?.phase || "");

    if (previousPhase === "closed" || previousPhase === "cancelled") {
      return;
    }

    if (nextPhase === "closed" || nextPhase === "cancelled") {
      map.set(order.id, order);
      return;
    }

    const previousTimestamp = new Date(getOrderTimestamp(previousOrder) || 0).getTime();
    const nextTimestamp = new Date(getOrderTimestamp(order) || 0).getTime();

    if (nextTimestamp >= previousTimestamp) {
      map.set(order.id, order);
    }
  });

  return Array.from(map.values());
};

const isCancelledOrder = (order) => {
  const phase = String(order?.phase || order?.estado || "")
    .trim()
    .toLowerCase();

  return phase === "cancelled" || phase === "cancelado";
};

const ensureDayEntry = (dailyMap, dayKey, date) => {
  if (!dailyMap.has(dayKey)) {
    dailyMap.set(dayKey, {
      id: dayKey,
      date: new Date(date),
      label: date.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
      }),
      weekday: DAY_LABELS[date.getDay()],
      ordersCount: 0,
      salesAmount: 0,
      collectedAmount: 0,
      retainedAmount: 0,
      netAmount: 0,
      platformFeesAmount: 0,
    });
  }

  return dailyMap.get(dayKey);
};

const getVisibleDailyRows = (dailyMap, window, nowDate) => {
  const rows = Array.from(dailyMap.values()).sort((left, right) => left.date - right.date);

  if (rows.length > 0) {
    return rows;
  }

  if (!window?.start || !window?.end || window.id === "all") {
    return [];
  }

  const fallbackRows = [];
  let cursor = new Date(window.start);

  while (cursor < window.end) {
    fallbackRows.push({
      id: formatDayKey(cursor),
      date: new Date(cursor),
      label: cursor.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
      }),
      weekday: DAY_LABELS[cursor.getDay()],
      ordersCount: 0,
      salesAmount: 0,
      collectedAmount: 0,
      retainedAmount: 0,
      netAmount: 0,
      platformFeesAmount: 0,
    });
    cursor = addDays(cursor, 1);
  }

  if (fallbackRows.length === 1 && window.id === "today") {
    return fallbackRows;
  }

  return fallbackRows.slice(-7);
};

export const buildStatisticsSnapshot = (
  ordersState,
  paymentOperations,
  rangeId = "today",
  nowDate = new Date()
) => {
  const window = buildRangeWindow(rangeId, nowDate);
  const settledOperations = (Array.isArray(paymentOperations) ? paymentOperations : []).filter(
    (operation) =>
      PAYMENT_STATUS_CASHED.has(String(operation?.status || "").toLowerCase())
  );
  const allOrders = dedupeOrders([
    ...(ordersState?.pedidosHistoricos || []),
    ...(ordersState?.mesasActivas || []),
  ]);
  const filteredOrders = allOrders.filter((order) => {
    if (!isWithinWindow(getOrderTimestamp(order), window)) {
      return false;
    }

    return !isCancelledOrder(order);
  });
  const paidOperations = settledOperations.filter(
    (operation) =>
      isWithinWindow(getPaymentTimestamp(operation), window)
  );

  const estimatedSales = filteredOrders.reduce((sum, order) => sum + getOrderTotal(order), 0);
  const collectedAmount = paidOperations.reduce(
    (sum, operation) => sum + getPaymentGrossAmount(operation),
    0
  );
  const platformFeesGenerated = paidOperations.reduce(
    (sum, operation) =>
      sum + toSafeNumber(getPaymentEconomics(operation)?.platformFeeAmount),
    0
  );
  const retainedAmount = paidOperations.reduce(
    (sum, operation) =>
      sum + toSafeNumber(getPaymentEconomics(operation)?.retainedAmount),
    0
  );
  const merchantNetSales = paidOperations.reduce(
    (sum, operation) =>
      sum + toSafeNumber(getPaymentEconomics(operation)?.merchantNetAmount),
    0
  );
  const merchantPayoutAmount = paidOperations.reduce(
    (sum, operation) =>
      sum + toSafeNumber(getPaymentEconomics(operation)?.payoutAmount),
    0
  );
  const pendingPlatformDebt = settledOperations.reduce(
    (sum, operation) =>
      sum + toSafeNumber(getPaymentEconomics(operation)?.pendingDebtAmount),
    0
  );
  const averageTicket = filteredOrders.length ? estimatedSales / filteredOrders.length : 0;
  const activeTablesNow = Array.isArray(ordersState?.mesasActivas)
    ? ordersState.mesasActivas.length
    : 0;
  const readyNow = (ordersState?.mesasActivas || []).filter((order) => order?.phase === "ready")
    .length;
  const pendingNow = (ordersState?.mesasActivas || []).filter((order) =>
    ["new", "preparation"].includes(String(order?.phase || ""))
  ).length;
  const openPaymentOperationsNow = (Array.isArray(paymentOperations) ? paymentOperations : [])
    .filter((operation) => !PAYMENT_STATUS_CASHED.has(String(operation?.status || "").toLowerCase()))
    .length;

  const dailyMap = new Map();
  const channelMap = new Map();
  const productMap = new Map();
  const adjustmentsMap = new Map();
  const paymentMethodMap = new Map();

  filteredOrders.forEach((order) => {
    const orderDate = new Date(getOrderTimestamp(order));
    if (Number.isNaN(orderDate.getTime())) {
      return;
    }

    const dayKey = formatDayKey(orderDate);
    const dayEntry = ensureDayEntry(dailyMap, dayKey, orderDate);
    const orderTotal = getOrderTotal(order);

    dayEntry.ordersCount += 1;
    dayEntry.salesAmount += orderTotal;

    const channelLabel = getOrderSourceLabel(order);
    const previousChannel = channelMap.get(channelLabel) || {
      id: channelLabel,
      label: channelLabel,
      ordersCount: 0,
      amount: 0,
    };
    previousChannel.ordersCount += 1;
    previousChannel.amount += orderTotal;
    channelMap.set(channelLabel, previousChannel);

    (Array.isArray(order?.items) ? order.items : []).forEach((item) => {
      const productName = getOrderProductName(item);
      const quantity = Math.max(1, toSafeNumber(item?.quantity || 1));
      const amount = getOrderItemTotal(item);
      const previousProduct = productMap.get(productName) || {
        id: productName,
        name: productName,
        quantity: 0,
        amount: 0,
      };
      previousProduct.quantity += quantity;
      previousProduct.amount += amount;
      productMap.set(productName, previousProduct);
    });

    (Array.isArray(order?.pricing?.adjustments) ? order.pricing.adjustments : []).forEach(
      (adjustment) => {
        const adjustmentKey = adjustment?.id || `${adjustment?.type || "adjustment"}:${adjustment?.title || "Ajuste"}`;
        const title = String(adjustment?.title || "Ajuste").trim() || "Ajuste";
        const type = adjustment?.type === "combo" ? "combo" : "promotion";
        const previousAdjustment = adjustmentsMap.get(adjustmentKey) || {
          id: adjustmentKey,
          title,
          type,
          uses: 0,
          amount: 0,
        };
        previousAdjustment.uses += 1;
        previousAdjustment.amount += toSafeNumber(adjustment?.amount);
        adjustmentsMap.set(adjustmentKey, previousAdjustment);
      }
    );
  });

  paidOperations.forEach((operation) => {
    const paymentDate = new Date(getPaymentTimestamp(operation));
    if (!Number.isNaN(paymentDate.getTime())) {
      const dayKey = formatDayKey(paymentDate);
      const dayEntry = ensureDayEntry(dailyMap, dayKey, paymentDate);
      dayEntry.collectedAmount += getPaymentGrossAmount(operation);
      dayEntry.platformFeesAmount += toSafeNumber(
        getPaymentEconomics(operation)?.platformFeeAmount
      );
      dayEntry.retainedAmount += toSafeNumber(
        getPaymentEconomics(operation)?.retainedAmount
      );
      dayEntry.netAmount += toSafeNumber(getPaymentEconomics(operation)?.merchantNetAmount);
    }

    const methodLabel = getPaymentMethodLabel(operation);
    const previousMethod = paymentMethodMap.get(methodLabel) || {
      id: methodLabel,
      label: methodLabel,
      count: 0,
      amount: 0,
      feeAmount: 0,
      retainedAmount: 0,
      netAmount: 0,
      debtAmount: 0,
    };
    previousMethod.count += 1;
    previousMethod.amount += getPaymentGrossAmount(operation);
    previousMethod.feeAmount += toSafeNumber(
      getPaymentEconomics(operation)?.platformFeeAmount
    );
    previousMethod.retainedAmount += toSafeNumber(
      getPaymentEconomics(operation)?.retainedAmount
    );
    previousMethod.netAmount += toSafeNumber(
      getPaymentEconomics(operation)?.merchantNetAmount
    );
    previousMethod.debtAmount += toSafeNumber(
      getPaymentEconomics(operation)?.pendingDebtAmount
    );
    paymentMethodMap.set(methodLabel, previousMethod);
  });

  return {
    rangeId,
    summary: {
      ordersCount: filteredOrders.length,
      estimatedSales,
      collectedAmount,
      platformFeesGenerated,
      retainedAmount,
      merchantNetSales,
      merchantPayoutAmount,
      pendingPlatformDebt,
      averageTicket,
      activeTablesNow,
      pendingNow,
      readyNow,
      openPaymentOperationsNow,
      combosDiscount: Array.from(adjustmentsMap.values())
        .filter((item) => item.type === "combo")
        .reduce((sum, item) => sum + item.amount, 0),
      promotionsDiscount: Array.from(adjustmentsMap.values())
        .filter((item) => item.type === "promotion")
        .reduce((sum, item) => sum + item.amount, 0),
    },
    dailyRows: getVisibleDailyRows(dailyMap, window, nowDate).map((row) => ({
      ...row,
      salesAmount: row.salesAmount,
      collectedAmount: row.collectedAmount,
      retainedAmount: row.retainedAmount,
      netAmount: row.netAmount,
      platformFeesAmount: row.platformFeesAmount,
    })),
    channelBreakdown: Array.from(channelMap.values()).sort((left, right) => right.amount - left.amount),
    paymentMethodBreakdown: Array.from(paymentMethodMap.values()).sort(
      (left, right) => right.amount - left.amount
    ),
    topProducts: Array.from(productMap.values())
      .sort((left, right) => {
        if (right.quantity !== left.quantity) {
          return right.quantity - left.quantity;
        }

        return right.amount - left.amount;
      })
      .slice(0, 8),
    adjustments: Array.from(adjustmentsMap.values())
      .sort((left, right) => right.amount - left.amount)
      .slice(0, 8),
  };
};

export const getRestaurantStatisticsSnapshot = (
  restaurant,
  rangeId = "today",
  nowDate = new Date()
) => {
  const ordersState = getOperationalOrdersState(restaurant, () => ({
    mesasActivas: [],
    pedidosHistoricos: [],
  }));
  const paymentOperations = getPaymentOperations(restaurant);

  return buildStatisticsSnapshot(ordersState, paymentOperations, rangeId, nowDate);
};
